import { ApiClientAxios, apiFactoryConfig } from "@repo/api";
import { useInfiniteQuery, type InfiniteData, type UseInfiniteQueryOptions } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { ms } from "humanize-ms";
import { useMemo } from "react";

export type UseFufiTransactionsPremiumsParams = {
    contractId: string;
    premiumsPageSize?: number;
    premiumsFrom?: Date;
    premiumsTo?: Date;
    options?: Partial<
        UseInfiniteQueryOptions<
            ApiClientAxios.FufiFinancialInfoResponse,
            unknown,
            InfiniteData<ApiClientAxios.FufiFinancialInfoResponse>,
            unknown[],
            number
        >
    >;
};

export const useFufiTransactionsPremiums = ({
    contractId,
    premiumsPageSize = 5,
    premiumsFrom,
    premiumsTo,
    options,
}: UseFufiTransactionsPremiumsParams) => {
    const api = ApiClientAxios.ContractsLifeApiFactory(...apiFactoryConfig);
    const toIso = (v?: Date) => (v ? formatISO(v) : undefined);

    const query = useInfiniteQuery<
        ApiClientAxios.FufiFinancialInfoResponse,
        unknown,
        InfiniteData<ApiClientAxios.FufiFinancialInfoResponse>,
        unknown[],
        number
    >({
        queryKey: ["FufiTransactionsPremiums", contractId, premiumsPageSize, toIso(premiumsFrom), toIso(premiumsTo)],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const request: ApiClientAxios.FufiFinancialInfoQuery = {
                premiumsPageSize,
                premiumsPage: pageParam,
                premiumsFrom: toIso(premiumsFrom),
                premiumsTo: toIso(premiumsTo),
            };

            return (await api.getLifeFufiFinancialInfoPost(contractId, request)).data;
        },
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const loaded = allPages.reduce((acc, page) => acc + (page.premiums?.length ?? 0), 0);
            if (lastPage.premiumsCount && loaded < lastPage.premiumsCount) {
                return lastPageParam + 1;
            }
            return undefined;
        },
        gcTime: ms("1h"),
        staleTime: ms("1h"),
        ...options,
    });

    const flatData = useMemo(
        () => (query?.data ? query.data.pages.flatMap((page) => page.premiums || []) : []),
        [query.data],
    );

    return {
        query,
        flatData,
    };
};
